import api from './api';

export type TipoConteudo = 'post_social' | 'video' | 'artigo' | 'email_blast';
export type EstadoConteudo = 'rascunho' | 'agendado' | 'publicado' | 'arquivado';

export interface Conteudo {
  id: number;
  titulo: string;
  tipo: TipoConteudo;
  plataforma: string | null;
  descricao: string | null;
  categoria: string | null;
  data_agendada: string | null;
  data_publicada: string | null;
  url_publicacao: string | null;
  url_midia: string | null;
  thumbnail: string | null;
  duracao_segundos: number | null;
  estado: EstadoConteudo;
  views: number;
  cliques: number;
  engagement: number;
  notas: string | null;
  created_at: string;
}

export interface PublishResult {
  message?: string;
  facebook?: { postId: string; url: string };
  instagram?: { mediaId: string; url: string };
  errors?: string[];
}

export interface ConteudoMetricas {
  views: number;
  engagement: number;
  cliques: number;
}

export interface SocialDiagnostico {
  token_configurado: boolean;
  page_id: string | null;
  ig_user_id: string | null;
  permissoes?: Record<string, boolean>;
  em_falta?: string[];
  permissoes_erro?: string;
}

export interface ConteudoStats {
  total: number;
  rascunhos: number;
  agendados: number;
  publicados: number;
  views_total: number;
  por_tipo: Array<{ tipo: TipoConteudo; n: number }>;
}

export interface ConteudoPayload {
  titulo: string;
  tipo?: TipoConteudo;
  plataforma?: string;
  descricao?: string;
  categoria?: string;
  data_agendada?: string;
  data_publicada?: string;
  url_publicacao?: string;
  url_midia?: string;
  thumbnail?: string;
  duracao_segundos?: number;
  estado?: EstadoConteudo;
  views?: number;
  cliques?: number;
  engagement?: number;
  notas?: string;
}

class MarketingConteudoService {
  async setup() { await api.post('/admin/marketing/conteudo/setup'); }
  async stats(): Promise<ConteudoStats> {
    const res = await api.get('/admin/marketing/conteudo/stats');
    return res.data.data;
  }
  async list(filters?: { tipo?: string; estado?: string; categoria?: string; search?: string }): Promise<Conteudo[]> {
    const qs = new URLSearchParams();
    Object.entries(filters || {}).forEach(([k, v]) => v && qs.append(k, v));
    const res = await api.get(`/admin/marketing/conteudo?${qs}`);
    return res.data.data || [];
  }
  async create(payload: ConteudoPayload): Promise<Conteudo> {
    const res = await api.post('/admin/marketing/conteudo', payload);
    return res.data.data;
  }
  async update(id: number, payload: Partial<ConteudoPayload>) {
    await api.put(`/admin/marketing/conteudo/${id}`, payload);
  }
  /** Publica mesmo nas redes (Facebook Page / Instagram Business). */
  async publicar(id: number): Promise<PublishResult> {
    const res = await api.post(`/admin/marketing/conteudo/${id}/publicar`, {});
    return { message: res.data.message, ...res.data.data };
  }

  /** Marca como publicado sem tocar nas redes — para conteúdo posto à mão. */
  async marcarPublicado(id: number, url_publicacao?: string) {
    await api.post(`/admin/marketing/conteudo/${id}/publicar`, {
      apenas_marcar: true,
      url_publicacao,
    });
  }

  async atualizarMetricas(id: number): Promise<ConteudoMetricas> {
    const res = await api.post(`/admin/marketing/social/${id}/metricas`);
    return res.data.data;
  }

  /** O que falta para conseguir publicar: token, IDs, permissões. */
  async diagnostico(): Promise<SocialDiagnostico> {
    const res = await api.get('/admin/marketing/social/diagnostico');
    return res.data.data;
  }
  async remove(id: number) {
    await api.delete(`/admin/marketing/conteudo/${id}`);
  }
}

export default new MarketingConteudoService();
